import { Component, ErrorHandler, HostListener, Inject, OnDestroy, OnInit, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { FieldTree, form, required } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'

import { LadataanService } from '../_jaettu-angular/service/ladataan.service'

import { MatSnackBar } from '@angular/material/snack-bar'
import { KirjanpidonPeruste } from '@lemon/core-firebase/model/kayttaja'
import { FirestoreTosite } from '@lemon/core-firebase/model/tosite'
import { MaksutService } from '@lemon/core-firebase/service/maksut.service'
import { TositeKopioija } from '@lemon/core-firebase/service/tosite/tosite.kopioija'
import { DateService } from '@lemon/core/service/date.service'
import { KayttajaService } from 'app/_angular/service/kayttaja.service'
import { TositeService } from 'app/_angular/service/tosite/tosite.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { Subject, takeUntil } from 'rxjs'

interface MaksupaivaLomake {
  tositteenPvm: Date | null
}

export interface MaksutMaksupaivaDialogData {
  kuitti: FirestoreTosite
  kirjanpidonPeruste: KirjanpidonPeruste
}

@Component({
  templateUrl: './maksut-maksupaiva.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MaksutMaksupaivaDialog implements OnInit, OnDestroy {
  private _ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)

  voidaanMitatoida = false

  lomake = form(signal<MaksupaivaLomake>({ tositteenPvm: null }), polku => {
    required(polku.tositteenPvm)
  })

  maxDate: Date = new Date(2099, 11, 31)
  minDate: Date = new Date(2010, 0, 1)

  private _kuitti: FirestoreTosite = null

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: MaksutMaksupaivaDialogData,
    private _dialogRef: MatDialogRef<MaksutMaksupaivaDialog>,
    private _errorHandler: ErrorHandler,
    private _snackbar: MatSnackBar,
    private _ladataanService: LadataanService,
    private _translationService: LemonTranslationService,
    private _tositeKopioija: TositeKopioija,
    private _dateService: DateService,
    private _tositeService: TositeService,
    private _formValidationService: FormValidationService,
    private _timestampService: TimestampService,
    private _maksutService: MaksutService,
    private _kayttajaService: KayttajaService,
  ) {
    this._kuitti = this._tositeKopioija.kopioiTosite(data.kuitti)
    this.maxDate = this._dateService.lisaaKuukausia(new Date(), 12)
    this.minDate = this._dateService.lisaaKuukausia(new Date(), -24)
  }

  ngOnInit() {
    arvonMuutokset(this.lomake.tositteenPvm, { injector: this._injector })
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        this._kuitti.paymentDate = this._dateService.dateToNumber(value)
      })

    asetaArvo(this.lomake.tositteenPvm, new Date())
  }

  @HostListener('document:keydown.enter')
  merkitseMaksetuksi() {
    this.tallenna()
  }

  private _saveInFlight = false
  async tallenna() {
    if (await this._kayttajaService.kayttajaaImpersonoidaan()) {
      return
    }

    if (!this.lomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    if (this._saveInFlight) {
      return
    }
    this._saveInFlight = true

    this._ladataanService.aloitaLataaminen()
    try {
      if (this.data.kirjanpidonPeruste === KirjanpidonPeruste.MAKSU) {
        const paymentDateLocal = this._dateService.numberToLocalDate(this._kuitti.paymentDate)
        const paymentDateTs = this._timestampService.localDateToTimestamp(paymentDateLocal)

        this._kuitti.p = this._kuitti.paymentDate
        this._kuitti.pvm = paymentDateTs
        this._kuitti.localPvm = paymentDateLocal
      }
      await this._tositeService.tallennaKuitti(this._kuitti)
      this._dialogRef.close()
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open(this._translationService.lokalisoi('yleiset.tuntematon-virhe'), 'OK', { duration: 10000 })
    } finally {
      this._saveInFlight = false
      this._ladataanService.lopetaLataaminen()
    }
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  kaytaErapaivaa() {
    this._kuitti.paymentDate = this._kuitti.dueDate
    asetaArvo(this.lomake.tositteenPvm, this._dateService.numberToDate(this._kuitti.paymentDate))
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }
}
